import * as React from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getMyAdminStatus } from "@/lib/billing.functions";
import {
  listCreditLedger,
  LEDGER_SOURCES,
  type LedgerRow,
  type LedgerTeamInfo,
  type LedgerUserInfo,
} from "@/lib/admin-ledger.functions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authed/admin/ledger")({
  head: () => ({
    meta: [
      { title: "Admin ledger: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: LedgerPage,
});

function LedgerPage() {
  const fetchLedger = useServerFn(listCreditLedger);
  const [teamId, setTeamId] = React.useState("");
  const [source, setSource] = React.useState<string>("");

  const query = useQuery({
    queryKey: ["admin-credit-ledger", teamId || null, source || null],
    queryFn: () =>
      fetchLedger({
        data: {
          teamId: teamId.trim() || null,
          source: (source || null) as (typeof LEDGER_SOURCES)[number] | null,
          limit: 500,
        },
      }),
  });

  const rows: LedgerRow[] = query.data?.rows ?? [];
  const teams: Record<string, LedgerTeamInfo> = query.data?.teams ?? {};
  const users: Record<string, LedgerUserInfo> = query.data?.users ?? {};

  // Per-team running-balance sanity check. Walk each team's rows oldest to
  // newest and flag any entry whose balance_after does not equal the
  // previous balance_after plus its amount.
  const flagged = React.useMemo(() => {
    const byTeam = new Map<string, LedgerRow[]>();
    for (const r of rows) {
      const list = byTeam.get(r.team_id) ?? [];
      list.push(r);
      byTeam.set(r.team_id, list);
    }
    const bad = new Set<string>();
    for (const list of byTeam.values()) {
      const asc = [...list].sort((a, b) =>
        a.created_at.localeCompare(b.created_at),
      );
      for (let i = 1; i < asc.length; i++) {
        const prev = asc[i - 1];
        const cur = asc[i];
        if (cur.balance_after !== prev.balance_after + cur.amount) {
          bad.add(cur.id);
        }
      }
    }
    return bad;
  }, [rows]);

  return (
    <main className="mx-auto max-w-[1200px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to admin
        </Link>
        <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Credit ledger.
        </h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          Append-only history of every credit balance change. Newest first.
          Rows highlighted in red do not reconcile with the previous balance
          for that team.
        </p>
      </header>

      <section className="mb-4 grid gap-3 sm:grid-cols-[1fr_240px]">
        <div>
          <Label htmlFor="team-filter">Team id (uuid, optional)</Label>
          <Input
            id="team-filter"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            autoComplete="off"
          />
        </div>
        <div>
          <Label htmlFor="source-filter">Source</Label>
          <select
            id="source-filter"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="h-9 w-full rounded-md border border-brand-line bg-brand-paper px-3 text-[13px]"
          >
            <option value="">All sources</option>
            {LEDGER_SOURCES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </section>

      {query.isLoading ? (
        <p className="text-[14px] text-brand-muted">Loading…</p>
      ) : query.error ? (
        <p className="text-[12.5px] text-destructive">
          {query.error instanceof Error ? query.error.message : "Load failed"}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-[14px] text-brand-muted">No ledger rows match.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-brand-line">
          <table className="w-full text-[12.5px]">
            <thead className="bg-brand-paper-2 text-left text-[11px] uppercase tracking-wider text-brand-muted">
              <tr>
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">Team</th>
                <th className="px-3 py-2">Acting user</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right">Balance after</th>
                <th className="px-3 py-2">Reference</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const team = teams[r.team_id];
                const user = r.user_id ? users[r.user_id] : null;
                return (
                <tr
                  key={r.id}
                  className={`border-t border-brand-line ${
                    flagged.has(r.id)
                      ? "bg-destructive/10 text-destructive"
                      : ""
                  }`}
                >
                  <td className="px-3 py-1.5 whitespace-nowrap font-mono">
                    {new Date(r.created_at).toISOString().replace("T", " ").slice(0, 19)}Z
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="text-brand-ink">
                      {team?.name ?? "(no team name)"}
                    </div>
                    <div className="font-mono text-[10.5px] text-brand-muted">
                      {r.team_id.slice(0, 8)}…
                    </div>
                  </td>
                  <td className="px-3 py-1.5">
                    {r.user_id ? (
                      <>
                        <div className="text-brand-ink">
                          {user?.email ?? "(unknown email)"}
                          {user?.name ? ` · ${user.name}` : ""}
                        </div>
                        <div className="font-mono text-[10.5px] text-brand-muted">
                          {r.user_id.slice(0, 8)}…
                        </div>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-1.5">{r.source}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {r.amount > 0 ? `+${r.amount}` : r.amount}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {r.balance_after}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">
                    {r.reference ?? "—"}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}